import type { OnResourceSaveCallback } from '@ctw/shared/api/fhir/action-helper';
import { getFetchFromFqs } from '@ctw/shared/api/fqs/client';
import { Link } from '@ctw/shared/components/link';
import { notify } from '@ctw/shared/components/toast';
import { UserPatientSummarizationProvider } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { BuilderConfigProvider } from '@ctw/shared/context/builder-config-context';
import { DrawerProvider } from '@ctw/shared/context/drawer-context';
import { ModalProvider } from '@ctw/shared/context/modal-context';
import {
  type TelemetryState,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { TelemetryInitializer } from '@ctw/shared/context/telemetry/telemetry-initializer';
import {
  WritebackProvider,
  type WritebackProviderProps,
} from '@ctw/shared/context/writeback-provider';
import type { ZusService } from '@ctw/shared/context/zui-provider';
import { useBrowserCompatibility } from '@ctw/shared/hooks/use-browser-compatibility';
import {
  getAuthTokenState,
  isAuthTokenExpiringWithin,
  isAuthTokenPastExpiryGracePeriod,
  type ZusAuthTokenState,
} from '@ctw/shared/utils/auth';
import { getAuthTokenDiagnostics } from '@ctw/shared/utils/auth-token-diagnostics';
import { AUTH_TOKEN_REFRESH_BUFFER_SECONDS } from '@ctw/shared/utils/auth-token-refresh';
import {
  currentHostnameIsLocalDevEnvironment,
  type Env,
  getApiServiceBaseUrl,
  getEnvFromCurrentHostname,
} from '@ctw/shared/utils/environments';
import { RequestError } from '@ctw/shared/utils/error';
import { assertResponseOK, getResponseContent } from '@ctw/shared/utils/http';
import { tw } from '@ctw/shared/utils/tailwind';
import { QueryCache, QueryClient } from '@tanstack/query-core';
import { QueryClientProvider, type QueryClientProviderProps } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GraphQLClient } from 'graphql-request';
import { debounce, upperFirst } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { BulkActionProvider } from './bulk-action-provider';
import { FeatureFlagProvider, type UnleashFeatureOverrides } from './feature-flag-provider';
import { ToastProvider } from './toast-provider';

// We use an expiry padding to provide a buffer to prevent race conditions.
// A race condition could happen in that we check if the token is expired,
// it isn't, but by time we do our request(s) it has expired.
// We track the id of the toast that we open to avoid stacking.
const AUTH_ERROR_TOAST_ID = 'invalid-token' as const;

const AUTH_TOKEN_INVALID_TOAST_LOG_MESSAGE = 'Invalid auth token toast shown';

// Caps how often the invalid-auth-token diagnostic re-logs for the same outcome while the user
// stays stuck; a change in outcome always logs immediately regardless of this interval.
const AUTH_TOKEN_LOG_HEARTBEAT_MS = 5 * 60 * 1000;

export const CTW_REQUEST_HEADER = { 'Zus-Request-Source': 'component-library' } as const;

export type CtwFetchFn = <TData>(
  url: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
) => Promise<{
  data: TData;
  ok: boolean;
  status: number;
  headers: Response['headers'];
}>;

export interface CTWRequestContext {
  isInIframe: boolean;
  env: Env;
  headers?: HeadersInit;
  authToken: string;
  ehr: string;
  builderId: string;
  service: ZusService;
  serviceVersion: string;
  serviceVariant?: string;
  serviceEnv: Env;
  isAuthTokenExpired: () => boolean;
  authTokenState: ZusAuthTokenState;
  ehrContext?: Record<string, string>;
}

export interface CTWState {
  requestContext: CTWRequestContext;
  headers?: HeadersInit;
  allowSmallBreakpointCCDAViewer: boolean;
  ctwBehavioralFlags: Record<string, boolean>;
  onResourceSave?: OnResourceSaveCallback;
  ehrContext?: Record<string, string>;
  updateBuilderId: (builderId: string) => void;
  graphqlClient: GraphQLClient;
  /*
   * @deprecated This method should not be used and will be replaced soon.
   */
  fetchFromFqs: (
    url: string,
    options: RequestInit,
  ) => Promise<{ data: unknown; headers: Response['headers'] }>;
  ctwFetch: CtwFetchFn;
}

export const CTWContext = createContext<CTWState | undefined>(undefined);

export interface CTWDevProps {
  fallbackEnv: Env;
}

export interface AuthTokenRefreshFailureDetail {
  errorCode?: string;
  endpoint?: string;
  status?: number;
}

export interface AuthTokenRefreshResult {
  token: string;
  failureDetail?: AuthTokenRefreshFailureDetail;
  // Set when refresh could not proceed because the credential it depends on (e.g. an upstream
  // launch token) has itself expired, as distinct from the exchange failing outright.
  expiredCredentialDetail?: AuthTokenRefreshFailureDetail;
}

export type CTWProviderProps = {
  devProps?: CTWDevProps;
  service: ZusService;
  serviceVersion: string;
  serviceVariant?: string;
  builderId?: string;
  ehr: string;
  headers?: Record<string, string>;
  authToken: string;
  getRefreshedAuthToken?: (token: string) => Promise<AuthTokenRefreshResult>;
  onAuthTokenExpiration?: () => void;
  ctwBehavioralFlags?: Record<string, boolean>;
  unleashFeatureOverrides?: UnleashFeatureOverrides;
  onResourceSave?: OnResourceSaveCallback;
  writebacks?: WritebackProviderProps;
  ehrContext?: Record<string, string>;
  allowSmallBreakpointCCDAViewer?: boolean;
};

/**
 * CTWProvider is required for all apps in the monorepo. It should be loaded as early as possible
 * and provides global context and telemetry for the app.
 */
export const CTWProvider = ({
  devProps,
  children,
  builderId,
  ehr,
  service,
  serviceVersion,
  serviceVariant,
  ctwBehavioralFlags = {},
  unleashFeatureOverrides = {},
  headers,
  authToken: initialAuthToken,
  onResourceSave,
  getRefreshedAuthToken,
  onAuthTokenExpiration,
  ehrContext,
  allowSmallBreakpointCCDAViewer = false,
  writebacks,
}: PropsWithChildren<CTWProviderProps>) => {
  const telemetry = useTelemetry();
  const telemetryRef = useRef(telemetry);
  telemetryRef.current = telemetry;
  const pageLoadEpochMsRef = useRef(performance.timeOrigin);
  // Holds since the page loaded until the first visibilitychange, whichever visibility state
  // that turns out to be.
  const lastVisibilityChangeEpochMsRef = useRef(performance.timeOrigin);
  // Persist across the refresh interval effect's teardown/recreation (triggered by
  // showingAuthTokenError toggling) so the throttle below survives every guard-reopen cycle.
  const lastLoggedAuthTokenOutcomeRef = useRef<
    AuthTokenRefreshAttemptDiagnostics['outcome'] | undefined
  >(undefined);
  const lastLoggedAuthTokenLogEpochMsRef = useRef<number | undefined>(undefined);
  const suppressedAuthTokenLogCountRef = useRef(0);

  useEffect(() => {
    const handleVisibilityChange = () => {
      lastVisibilityChangeEpochMsRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const [authToken, setAuthToken] = useState(initialAuthToken);
  const [authTokenState, setAuthTokenState] = useState(getAuthTokenState(authToken));
  const [showingAuthTokenError, setShowingAuthTokenError] = useState(false);
  const [currentBuilderId, setCurrentBuilderId] = useState(builderId ?? authTokenState.builderId);

  // Adopts a changed `authToken` prop (a host delivering a new token to a surface with no
  // getRefreshedAuthToken, such as the ZAP iframe) without clobbering a token this provider
  // refreshed on its own. Comparing against the last prop seen, rather than the current
  // authToken state, is what keeps those two update paths from fighting each other: an
  // internal refresh changes authToken but not the prop, so it never trips this check.
  const [lastSeenAuthTokenProp, setLastSeenAuthTokenProp] = useState(initialAuthToken);
  if (initialAuthToken !== lastSeenAuthTokenProp) {
    setLastSeenAuthTokenProp(initialAuthToken);
    setAuthToken(initialAuthToken);
    const adoptedAuthTokenState = getAuthTokenState(initialAuthToken);
    setAuthTokenState(adoptedAuthTokenState);
    setCurrentBuilderId(builderId ?? adoptedAuthTokenState.builderId);
  }

  const [browserWarningShown, setBrowserWarningShown] = useState(false);
  const { isBrowserSupported, SUPPORTED_BROWSERS } = useBrowserCompatibility();
  const env = useMemo(
    () => getEnvFromCurrentHostname({ fallbackEnv: devProps?.fallbackEnv }),
    [devProps],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: The linter complains that getRefreshedAuthToken is not needed, but we do need to re-initialize this callback when it changes.
  const debouncedGetRefreshedAuthToken = useCallback(
    debounce(async (token) => getRefreshedAuthToken?.(token), 2000, { leading: true }),
    [getRefreshedAuthToken],
  );

  useEffect(() => {
    telemetry.logger.debug('Mounting CTWProvider', {
      env,
      ehr,
      builderId,
      service,
      serviceVersion,
      serviceVariant,
      ctwBehavioralFlags,
    });
  }, [
    builderId,
    ehr,
    env,
    ctwBehavioralFlags,
    service,
    serviceVariant,
    serviceVersion,
    telemetry.logger,
  ]);

  // The QueryClient must be created exactly once — recreating it destroys
  // the entire query cache and restarts every active query (including
  // long-running fqsRequestAll pagination loops).  We access telemetry
  // through a ref so the error handler always uses the latest instance
  // without being a useMemo dependency.
  const queryClient: QueryClientProviderProps['client'] = useMemo(
    (): QueryClientProviderProps['client'] =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            gcTime: 1000 * 30, // garbage collect data every 30s
            staleTime: 1000 * 30, // data is valid for 30s, stale while revalidate after
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            telemetryRef.current.trackError({
              message: 'Unhandled react query error',
              error,
              context: {
                query: {
                  key: query.queryKey,
                  status: query.state.status,
                },
                queryMeta: query.meta,
              },
            });
          },
        }),
      }) as unknown as QueryClientProviderProps['client'],
    [],
  );

  const requestContext: CTWRequestContext = useMemo(
    () => ({
      isInIframe: window.location !== window.parent.location,
      authToken,
      isAuthTokenExpired: () => isAuthTokenPastExpiryGracePeriod(authToken),
      authTokenState,
      builderId: currentBuilderId,
      ehr,
      env,
      headers,
      service,
      serviceEnv: currentHostnameIsLocalDevEnvironment() ? 'dev' : env,
      serviceVersion,
      serviceVariant,
      ehrContext,
    }),
    [
      authToken,
      authTokenState,
      currentBuilderId,
      ehr,
      env,
      headers,
      service,
      serviceVariant,
      serviceVersion,
      ehrContext,
    ],
  );

  const graphqlClient = useMemo(() => {
    const endpoint = `${getApiServiceBaseUrl({ service: 'fqs', env: requestContext.env })}/query`;

    return new GraphQLClient(endpoint, {
      errorPolicy: 'all',
      headers: {
        ...CTW_REQUEST_HEADER,
        authorization: `Bearer ${requestContext.authToken}`,
      },
    });
  }, [requestContext.authToken, requestContext.env]);

  const ctwFetch: CtwFetchFn = useCallback(
    async (input, init = {}) => {
      const newInit: Parameters<typeof fetch>[1] = {
        ...init,
        headers: {
          ...init.headers,
          ...CTW_REQUEST_HEADER,
        },
      };

      try {
        const fetchResponse = await fetch(input, newInit);
        await assertResponseOK(fetchResponse);

        return {
          data: await getResponseContent(fetchResponse),
          ok: fetchResponse.ok,
          status: fetchResponse.status,
          headers: fetchResponse.headers,
        };
      } catch (error) {
        if (!newInit.signal?.aborted) {
          telemetry.trackError({
            error,
            context: {
              fetch: {
                url: input.toString(),
                init: {
                  ...newInit,
                  headers: telemetry.redactSensitiveHttpHeaders(newInit.headers),
                },
              },
            },
          });
        }

        return {
          data: error instanceof RequestError ? error.data : {},
          ok: false,
          status: error instanceof RequestError ? error.statusCode : 0,
          headers: new Headers(),
        };
      }
    },
    [telemetry],
  );

  const providerState: CTWState = useMemo(
    () => ({
      requestContext,
      allowSmallBreakpointCCDAViewer,
      ctwBehavioralFlags,
      headers,
      onResourceSave,
      ehrContext,
      updateBuilderId: setCurrentBuilderId,
      fetchFromFqs: getFetchFromFqs(ctwFetch, env, authToken),
      graphqlClient,
      ctwFetch,
    }),
    [
      requestContext,
      allowSmallBreakpointCCDAViewer,
      ctwBehavioralFlags,
      headers,
      onResourceSave,
      ehrContext,
      env,
      authToken,
      graphqlClient,
      ctwFetch,
    ],
  );

  useLayoutEffect(() => {
    let isActive = true;
    const intervalId = setInterval(() => {
      const handleAuthTokenRefresh = async () => {
        if (isAuthTokenExpiringWithin(authToken, AUTH_TOKEN_REFRESH_BUFFER_SECONDS)) {
          const attemptedAtEpochMs = Date.now();
          const refreshResult = await debouncedGetRefreshedAuthToken(authToken);
          if (!isActive) {
            return;
          }
          const refreshedAuthToken = refreshResult?.token;
          const refreshedTokenState = getAuthTokenState(refreshedAuthToken ?? '');

          if (refreshedAuthToken && refreshedAuthToken !== authToken) {
            setAuthToken(refreshedAuthToken);
            setAuthTokenState(refreshedTokenState);
          } else if (
            (!(refreshedAuthToken && refreshedTokenState.email) ||
              isAuthTokenPastExpiryGracePeriod(refreshedAuthToken)) &&
            !showingAuthTokenError
          ) {
            onAuthTokenExpiration?.();
            sendInvalidTokenNotification(providerState.requestContext, AUTH_ERROR_TOAST_ID);

            const refreshAttempt: AuthTokenRefreshAttemptDiagnostics = {
              trigger: 'silent-renewal',
              attemptedAtEpochMs,
              outcome: !getRefreshedAuthToken
                ? 'skipped-no-refresh-fn'
                : refreshResult?.expiredCredentialDetail
                  ? 'id-token-expired'
                  : refreshResult?.failureDetail
                    ? 'failed'
                    : 'succeeded-token-rejected',
              occurrencesSuppressedSinceLastLog: suppressedAuthTokenLogCountRef.current,
              ...refreshResult?.failureDetail,
              ...refreshResult?.expiredCredentialDetail,
            };

            setShowingAuthTokenError(true);

            const outcomeChanged = lastLoggedAuthTokenOutcomeRef.current !== refreshAttempt.outcome;
            const heartbeatElapsed =
              lastLoggedAuthTokenLogEpochMsRef.current === undefined ||
              attemptedAtEpochMs - lastLoggedAuthTokenLogEpochMsRef.current >=
                AUTH_TOKEN_LOG_HEARTBEAT_MS;

            if (outcomeChanged || heartbeatElapsed) {
              logInvalidAuthTokenDiagnostics({
                telemetry: telemetryRef.current,
                requestContext: providerState.requestContext,
                refreshAttempt,
                pageLoadEpochMs: pageLoadEpochMsRef.current,
                lastVisibilityChangeEpochMs: lastVisibilityChangeEpochMsRef.current,
              });
              lastLoggedAuthTokenOutcomeRef.current = refreshAttempt.outcome;
              lastLoggedAuthTokenLogEpochMsRef.current = attemptedAtEpochMs;
              suppressedAuthTokenLogCountRef.current = 0;
            } else {
              suppressedAuthTokenLogCountRef.current += 1;
            }
          }
        }
      };

      void handleAuthTokenRefresh();
    }, 5_000);

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [
    authToken,
    debouncedGetRefreshedAuthToken,
    getRefreshedAuthToken,
    onAuthTokenExpiration,
    providerState.requestContext,
    showingAuthTokenError,
  ]);

  useLayoutEffect(() => {
    // We don't want to stack toast notifications. So we track if there is one already open with auth errors
    const unsubscribe = toast.onChange((t) => {
      if (t.id === AUTH_ERROR_TOAST_ID && t.status === 'removed') {
        setShowingAuthTokenError(false);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // Closes the invalid auth token toast once the token is a user token inside its grace period.
  useEffect(() => {
    if (getAuthTokenState(authToken).email && !isAuthTokenPastExpiryGracePeriod(authToken)) {
      toast.dismiss(AUTH_ERROR_TOAST_ID);
    }
  }, [authToken]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sendUnsupportedBrowserNotification intentionally omitted to run this browser-compatibility check only once on mount
  useEffect(() => {
    // Check browser compatibility
    if (!(browserWarningShown || isBrowserSupported())) {
      sendUnsupportedBrowserNotification();
      setBrowserWarningShown(true);
      telemetry.logger.info('Detected unsupported browser');
    }
  }, [isBrowserSupported, browserWarningShown, telemetry.logger]);

  const sendUnsupportedBrowserNotification = useCallback(() => {
    const browsersToDisplay = Array.from(SUPPORTED_BROWSERS)
      .filter((b) => !(b.startsWith('ios_') || b.startsWith('and_'))) // Filter out ugly iOS / Android browser strings
      .map(upperFirst);
    let browserList: string;
    switch (browsersToDisplay.length) {
      case 0:
        browserList = 'a supported browser';
        break;
      case 1:
        browserList = browsersToDisplay[0];
        break;
      case 2:
        browserList = `${browsersToDisplay[0]} or ${browsersToDisplay[1]}`;
        break;
      default:
        browserList = `${browsersToDisplay
          .slice(0, -1)
          .join(', ')}, or ${browsersToDisplay.slice(-1)}`;
    }

    notify({
      type: 'error',
      title: 'Unsupported Browser',
      body: (
        <>
          You are using an unsupported browser. For the best experience, please use the latest
          version of {browserList}. See our{' '}
          <Link
            href="https://docs.zushealth.com/docs/supported-browsers"
            target="_blank"
            className={tw`text-error-text underline`}
            telemetryObfuscatedIdentifier="Supported Browsers"
          >
            supported browsers documentation
          </Link>
          .
        </>
      ),
      options: {
        toastId: 'unsupported-browser',
      },
    });
  }, [SUPPORTED_BROWSERS]);

  return (
    <CTWContext.Provider value={providerState}>
      <TelemetryInitializer>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <ToastProvider>
            <FeatureFlagProvider env={env} unleashFeatureOverrides={unleashFeatureOverrides}>
              <WritebackProvider {...writebacks}>
                <BulkActionProvider>
                  <BuilderConfigProvider>
                    <ModalProvider>
                      {({ modal }) => (
                        <UserPatientSummarizationProvider>
                          <DrawerProvider allowDocking={false}>
                            {({ drawer }) => (
                              <>
                                {drawer}
                                {children}
                              </>
                            )}
                          </DrawerProvider>
                          {modal}
                        </UserPatientSummarizationProvider>
                      )}
                    </ModalProvider>
                  </BuilderConfigProvider>
                </BulkActionProvider>
              </WritebackProvider>
            </FeatureFlagProvider>
          </ToastProvider>
        </QueryClientProvider>
      </TelemetryInitializer>
    </CTWContext.Provider>
  );
};

const sendInvalidTokenNotification = (requestContext: CTWRequestContext, toastId: string) => {
  const message = ((): string => {
    if (requestContext.isAuthTokenExpired()) {
      return 'This token has expired';
    }

    const diagnostics = getAuthTokenDiagnostics(requestContext.authToken, requestContext.env);
    const isShapedLikeAUserToken =
      !diagnostics.isMalformed &&
      diagnostics.isIssuerAllowed &&
      diagnostics.isAudienceAllowed &&
      diagnostics.missingClaims.length === 0;

    return isShapedLikeAUserToken
      ? 'This token is near expiry and could not be refreshed'
      : 'Only user tokens are allowed';
  })();
  notify({
    type: 'error',
    title: 'Warning',
    body: `Invalid auth token: ${message}`,
    options: {
      toastId,
    },
  });
};

type AuthTokenRefreshAttemptDiagnostics = AuthTokenRefreshFailureDetail & {
  trigger: 'silent-renewal';
  attemptedAtEpochMs: number;
  outcome: 'failed' | 'skipped-no-refresh-fn' | 'succeeded-token-rejected' | 'id-token-expired';
  // How many same-outcome attempts were throttled since the last time this outcome was logged.
  occurrencesSuppressedSinceLastLog: number;
};

const logInvalidAuthTokenDiagnostics = ({
  telemetry,
  requestContext,
  refreshAttempt,
  pageLoadEpochMs,
  lastVisibilityChangeEpochMs,
}: {
  telemetry: TelemetryState;
  requestContext: CTWRequestContext;
  refreshAttempt: AuthTokenRefreshAttemptDiagnostics;
  pageLoadEpochMs: number;
  lastVisibilityChangeEpochMs: number;
}) => {
  const nowEpochMs = Date.now();

  telemetry.logger.warn(AUTH_TOKEN_INVALID_TOAST_LOG_MESSAGE, {
    token: getAuthTokenDiagnostics(requestContext.authToken, requestContext.env),
    refreshAttempt,
    pageLifecycle: {
      openDurationMs: nowEpochMs - pageLoadEpochMs,
      visibilityState: document.visibilityState,
      timeInCurrentVisibilityStateMs: nowEpochMs - lastVisibilityChangeEpochMs,
    },
    correlation: {
      dataDogSessionId: telemetry.getDataDogSessionId(),
      builderId: requestContext.builderId,
      app: requestContext.service,
      serviceVariant: requestContext.serviceVariant,
      ehr: requestContext.ehr,
      route: window.location.pathname,
    },
  });
};

export const useCTW = () => {
  const context = useContext(CTWContext);

  if (!context) {
    throw new Error('useCTW must be used within a CTWProvider');
  }

  return context;
};
